// Single source of truth for expert-hours pricing on the client.
// Prices display in dollars; the ledger moves in architect hours. Linear hour blocks are
// priced at USD_PER_HOUR, so their displayed dollars are always exact. Fixed-price
// services carry an explicit displayUSD because their marketing price is not a clean
// multiple of the hourly rate ($299 for 6h) — the server resolves the real charge from
// its own price list, so displayUSD is presentation only and never sent to an API.
// Keep SERVICE_COSTS in sync with Server/src/config/expert_services.py.

export const USD_PER_HOUR = 50;

// The least an architect can do anything meaningful in, and the floor for opening a
// development engagement. Fixed-price services set their own size and bypass this.
export const ENGAGEMENT_MIN_HOURS = 10;

// Purchasable units and redeemable units share this grid so the two never drift apart.
export const HOUR_GRID_STEP = 10;

export interface HourPackage {
  hours: number;
  tag?: string;
  blurb: string;
}

export const HOUR_PACKAGES: HourPackage[] = [
  { hours: 10, tag: "Most chosen", blurb: "The minimum to open an engagement." },
  { hours: 20, blurb: "Room for a second engagement, or a longer one." },
  { hours: 40, blurb: "About a week of architect time." },
  { hours: 80, blurb: "Two weeks of dedicated build time." },
];

export type ExpertServiceKey =
  | "development"
  | "migration"
  | "production_essential"
  | "production_advanced";

export interface ExpertServiceCost {
  /** Server-side SKU key; the server resolves price and hours from this. */
  sku: string;
  hours: number;
  /** Marketing price shown in the UI. Never sent to an API. */
  displayUSD: number;
  label: string;
  /** Sold as a month of cover; there is no auto-renewal, the user re-engages. */
  recurring?: boolean;
}

export const SERVICE_COSTS: Record<Exclude<ExpertServiceKey, "development">, ExpertServiceCost> = {
  migration: {
    sku: "migration",
    hours: 40,
    displayUSD: 2000,
    label: "Project migration",
  },
  production_essential: {
    sku: "production_essential",
    hours: 6,
    displayUSD: 299,
    label: "Essential",
    recurring: true,
  },
  production_advanced: {
    sku: "production_advanced",
    hours: 20,
    displayUSD: 999,
    label: "Advanced",
    recurring: true,
  },
};

export function toUSD(hours: number): number {
  return hours * USD_PER_HOUR;
}

export function formatUSD(usd: number): string {
  return `$${usd.toLocaleString("en-US")}`;
}

/** Dollar price of a linear hour block — exact by construction. */
export function formatHourPrice(hours: number): string {
  return formatUSD(toUSD(hours));
}

/** Hours reading, e.g. "6h" — trims the trailing zero on whole halves. */
export function formatHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

/** Smallest purchasable block that covers *shortfall*, falling back to the largest. */
export function hoursToCover(shortfall: number): number {
  const needed = Math.max(ENGAGEMENT_MIN_HOURS, Math.ceil(shortfall));
  const fit = HOUR_PACKAGES.find((p) => p.hours >= needed);
  return fit ? fit.hours : HOUR_PACKAGES[HOUR_PACKAGES.length - 1].hours;
}

export interface ArchitectHoursBalance {
  monthly_allocated?: number;
  monthly_used?: number;
  add_on_balance?: number;
}

/** Plan allocation left over plus purchased add-on hours. */
export function totalAvailableHours(hours?: ArchitectHoursBalance | null): number {
  if (!hours) return 0;
  return (hours.monthly_allocated || 0) - (hours.monthly_used || 0) + (hours.add_on_balance || 0);
}

/** Whether *balance* alone can open an engagement of *required* hours. */
export function canOpenEngagement(balance: number, required: number = ENGAGEMENT_MIN_HOURS): boolean {
  return Math.floor(balance * 10) / 10 >= required;
}
